import { useMemo, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useNavigate, Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, Pencil, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";

const POSITIONS = [
    { v: "dos", l: "Sur le dos" },
    { v: "cote", l: "Sur le côté" },
    { v: "ventre", l: "Sur le ventre" },
    { v: "change", l: "Je change souvent de position" },
    { v: "inconnu", l: "Je ne sais pas vraiment" },
];
const SENSATIONS = [
    { v: "enveloppante", l: "Très enveloppante" },
    { v: "moelleuse", l: "Moelleuse mais avec du soutien" },
    { v: "equilibree", l: "Équilibrée" },
    { v: "tonique", l: "Tonique" },
    { v: "ferme", l: "Ferme" },
    { v: "inconnu", l: "Je ne sais pas" },
];
const HEATS = [
    { v: "rarement", l: "Rarement" },
    { v: "parfois", l: "Parfois" },
    { v: "souvent", l: "Souvent" },
    { v: "tres_souvent", l: "Très souvent" },
];
const PARTNER = [
    { v: "pas", l: "Pas du tout" },
    { v: "peu", l: "Un peu" },
    { v: "souvent", l: "Souvent" },
    { v: "beaucoup", l: "Beaucoup" },
];
const MOVEMENT = [
    { v: "peu", l: "Peu important" },
    { v: "assez", l: "Assez important" },
    { v: "tres", l: "Très important" },
];
const CURRENT_TYPES = ["Mousse", "Mémoire de forme", "Ressorts", "Latex", "Hybride", "Je ne sais pas"];
const CHANGE_REASONS = [
    "Il est trop ferme",
    "Il est trop mou",
    "Il s'affaisse",
    "J'ai trop chaud",
    "Je sens les mouvements de mon partenaire",
    "Je dors moins bien qu'avant",
    "Il est ancien",
    "Je souhaite simplement plus de confort",
    "Autre",
];
const DISCOMFORT = ["Non", "Épaules", "Haut du dos", "Bas du dos", "Hanches", "Autre"];
const DIMENSIONS = ["80 × 200", "90 × 190", "90 × 200", "140 × 190", "140 × 200", "160 × 200", "180 × 200", "200 × 200", "2 × 80 × 200", "2 × 90 × 200", "Autre"];
const OTHER_TEXT_MAX = 200;
const DIM_MIN = 60;
const DIM_MAX = 260;

const initialSleeper = (label) => ({
    label,
    taille: "",
    poids: "",
    position: "",
    sensation: "",
    envelopment: null,
    movement_ease: "",
    heat: "",
    discomfort_areas: [],
    discomfort_other: "",
    partner: "",
});

const PHONE_FR_RE = /^(?:(?:\+33|0033)\s?[1-9](?:[\s.-]?\d{2}){4}|0[1-9](?:[\s.-]?\d{2}){4})$/;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const validPhone = (v) => !v || PHONE_FR_RE.test(v.trim());
const validEmail = (v) => !v || EMAIL_RE.test(v.trim());

// Phase 4: labels lookup used by review page & contradiction messages.
const LABEL = {
    position: Object.fromEntries(POSITIONS.map((p) => [p.v, p.l])),
    sensation: Object.fromEntries(SENSATIONS.map((p) => [p.v, p.l])),
    heat: Object.fromEntries(HEATS.map((p) => [p.v, p.l])),
    partner: Object.fromEntries(PARTNER.map((p) => [p.v, p.l])),
    movement: Object.fromEntries(MOVEMENT.map((p) => [p.v, p.l])),
};

// Phase 4: Enforce exclusive-answer rules on the discomfort multi-select.
// "Non" is mutually exclusive with every body area; selecting body areas
// clears "Non", and selecting "Non" clears every other choice.
const applyDiscomfortExclusive = (previous, next) => {
    const wasNon = previous.includes("Non");
    const nowHasNon = next.includes("Non");
    if (nowHasNon && !wasNon) return ["Non"]; // just picked "Non" → drop others
    if (nowHasNon && next.length > 1) return next.filter((v) => v !== "Non"); // picked something else while "Non" was there
    return next;
};

// Phase 4: Contradiction detector — returns an array of human-readable labels.
// Never mutates answers; only surfaces the modal.
const detectContradictions = (sleepers, state, nbSleepers) => {
    const out = [];
    // 1) matelas actuel : trop ferme ET trop mou
    const reasons = state.change_reasons || [];
    if (reasons.includes("Il est trop ferme") && reasons.includes("Il est trop mou")) {
        out.push("Vous indiquez à la fois que votre matelas actuel est trop ferme et trop mou.");
    }
    // 2..4) per-sleeper rules
    sleepers.forEach((s, i) => {
        const who = nbSleepers === 2 ? `Dormeur ${i + 1} — ` : "";
        if (s.sensation === "enveloppante" && (s.heat === "souvent" || s.heat === "tres_souvent")) {
            out.push(`${who}vous préférez une sensation très enveloppante mais indiquez avoir souvent chaud.`);
        }
        if (s.sensation === "enveloppante" && s.movement_ease === "tres") {
            out.push(`${who}vous préférez une sensation très enveloppante et un besoin très important de liberté de mouvement.`);
        }
        if (s.sensation === "ferme" && Array.isArray(s.discomfort_areas)) {
            const painful = s.discomfort_areas.some((a) => a === "Épaules" || a === "Hanches");
            if (painful) {
                out.push(`${who}vous préférez une sensation ferme malgré des gênes signalées aux épaules ou aux hanches.`);
            }
        }
    });
    // 5) trop de "Je ne sais pas" → informations insuffisantes
    const unknownCount = sleepers.reduce((n, s) => n + (s.position === "inconnu" ? 1 : 0) + (s.sensation === "inconnu" ? 1 : 0), 0);
    if (unknownCount >= 2) {
        out.push("Plusieurs réponses « Je ne sais pas » ont été enregistrées — la recommandation pourrait manquer de précision.");
    }
    return out;
};

// Phase 4: RFC4122-ish random id fallback when crypto.randomUUID is missing.
function cryptoRandomId() {
    try { if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID(); } catch { /* ignore */ }
    const rand = () => Math.random().toString(16).slice(2, 10);
    return `${rand()}-${rand()}-${rand()}-${rand()}`;
}

const HEAT_ORDER = ["rarement", "parfois", "souvent", "tres_souvent"];
const MOVE_ORDER = ["peu", "assez", "tres"];
const PARTNER_ORDER = ["pas", "peu", "souvent", "beaucoup"];
const maxByOrder = (vals, order) => {
    const filtered = vals.filter(Boolean);
    if (filtered.length === 0) return null;
    return filtered.reduce((a, b) => (order.indexOf(a) >= order.indexOf(b) ? a : b));
};

export default function Questionnaire() {
    const navigate = useNavigate();
    const [nbSleepers, setNbSleepers] = useState(null); // 1 or 2
    const [sleepers, setSleepers] = useState([initialSleeper("Dormeur 1")]);
    const [state, setState] = useState({
        current_mattress_type: "",
        current_mattress_no_answer: false,
        change_reasons: [],
        change_reason_other: "",
        dimension: "",
        dimension_custom_largeur: "",
        dimension_custom_longueur: "",
        budget: "",
        budget_priority_confort: false,
        prenom: "",
        nom: "",
        telephone: "",
        email: "",
    });
    const [step, setStep] = useState(0);
    const [submitting, setSubmitting] = useState(false);
    const [isTransitioning, setIsTransitioning] = useState(false);
    // Phase 4: Review page (shown after contact step, before submit).
    const [showReview, setShowReview] = useState(false);
    // Phase 4: Contradiction modal (open when contradictions are detected).
    const [contradictionsOpen, setContradictionsOpen] = useState(false);
    const [detectedContradictions, setDetectedContradictions] = useState([]);
    // Phase 4: Stable client-side submission_id — regenerated only after a
    // successful submit or a user-initiated "start over".
    const submissionIdRef = useRef(cryptoRandomId());
    // Debounce absolu (indépendant de framer-motion) : garantit un intervalle
    // minimum entre deux navigations, même si l'animation se termine plus tôt.
    const lastNavAt = useRef(0);
    const NAV_MIN_INTERVAL_MS = 450;

    const steps = useMemo(() => {
        const s = [
            "nb_sleepers",
            "morphology",
            "position",
            "sensation",
            "envelopment",
            "movement_ease",
            "heat",
        ];
        if (nbSleepers === 2) s.push("partner");
        s.push("current_mattress", "discomfort", "dimension", "budget", "contact");
        return s;
    }, [nbSleepers]);

    const current = steps[step];
    // Tant que le nombre de dormeurs n'est pas choisi, on n'affiche PAS un
    // faux total : simplement « Étape 1 ». Le total réel apparaît après.
    const showTotal = nbSleepers !== null;
    const totalDisplayed = nbSleepers === 1 ? steps.length : 13;
    const progress = showTotal ? ((step + 1) / totalDisplayed) * 100 : (1 / 13) * 100;

    const updateSleeper = (idx, patch) =>
        setSleepers((prev) => prev.map((s, i) => (i === idx ? { ...s, ...patch } : s)));

    const setNb = (n) => {
        setNbSleepers(n);
        setSleepers(
            n === 2
                ? [initialSleeper("Dormeur 1"), initialSleeper("Dormeur 2")]
                : [initialSleeper("Dormeur 1")]
        );
    };

    const nonEmpty = (v) => typeof v === "string" && v.trim().length > 0;
    const inDimRange = (v) => {
        const n = Number(v);
        return Number.isFinite(n) && n >= DIM_MIN && n <= DIM_MAX;
    };

    const canNext = () => {
        switch (current) {
            case "nb_sleepers": return nbSleepers !== null;
            case "morphology": return sleepers.every((s) => Number(s.taille) >= 100 && Number(s.taille) <= 230 && Number(s.poids) >= 30 && Number(s.poids) <= 200);
            case "position": return sleepers.every((s) => s.position);
            case "sensation": return sleepers.every((s) => s.sensation);
            case "envelopment": return sleepers.every((s) => s.envelopment !== null && s.envelopment !== undefined);
            case "movement_ease": return sleepers.every((s) => s.movement_ease);
            case "heat": return sleepers.every((s) => s.heat);
            case "partner": return sleepers.every((s) => s.partner);
            case "current_mattress":
                if (state.current_mattress_no_answer) return true;
                if (!state.current_mattress_type || (state.change_reasons || []).length === 0) return false;
                if ((state.change_reasons || []).includes("Autre") && !nonEmpty(state.change_reason_other)) return false;
                return true;
            case "discomfort":
                // Any sleeper with "Autre" selected must provide a non-empty precision.
                return sleepers.every((s) => !(s.discomfort_areas || []).includes("Autre") || nonEmpty(s.discomfort_other));
            case "dimension":
                if (!state.dimension) return false;
                if (state.dimension === "Autre") {
                    return inDimRange(state.dimension_custom_largeur) && inDimRange(state.dimension_custom_longueur);
                }
                return true;
            case "budget":
                return state.budget_priority_confort || (Number(state.budget) > 0);
            case "contact":
                return validPhone(state.telephone) && validEmail(state.email);
            default: return true;
        }
    };

    const next = () => {
        const now = Date.now();
        if (now - lastNavAt.current < NAV_MIN_INTERVAL_MS) return;
        if (!canNext() || isTransitioning || submitting) return;
        lastNavAt.current = now;
        // Phase 4: last question step opens the review page instead of submitting.
        if (step === steps.length - 1) { setShowReview(true); return; }
        setIsTransitioning(true);
        setStep((s) => s + 1);
    };
    const prev = () => {
        const now = Date.now();
        if (now - lastNavAt.current < NAV_MIN_INTERVAL_MS) return;
        if (isTransitioning || submitting) return;
        if (step === 0) return;
        lastNavAt.current = now;
        setIsTransitioning(true);
        setStep((s) => Math.max(0, s - 1));
    };

    // Phase 4: build a stable, sanitised payload (identical logic used by the
    // review page display and the final submission call).
    const buildPayload = () => {
        const heatAgg = maxByOrder(sleepers.map((s) => s.heat), HEAT_ORDER) || "parfois";
        const moveAgg = maxByOrder(sleepers.map((s) => s.movement_ease), MOVE_ORDER) || "assez";
        const partnerAgg = maxByOrder(sleepers.map((s) => s.partner), PARTNER_ORDER);
        const discomfortUnion = Array.from(new Set(sleepers.flatMap((s) => s.discomfort_areas || [])));

        const otherTexts = {};
        if ((state.change_reasons || []).includes("Autre") && nonEmpty(state.change_reason_other)) {
            otherTexts.change_reason = state.change_reason_other.trim().slice(0, OTHER_TEXT_MAX);
        }
        const discomfortOther = {};
        sleepers.forEach((s, i) => {
            if ((s.discomfort_areas || []).includes("Autre") && nonEmpty(s.discomfort_other)) {
                discomfortOther[String(i)] = s.discomfort_other.trim().slice(0, OTHER_TEXT_MAX);
            }
        });
        if (Object.keys(discomfortOther).length > 0) otherTexts.discomfort = discomfortOther;

        let finalDimension = state.dimension || null;
        let dimensionCustom = null;
        if (state.dimension === "Autre") {
            const lar = Number(state.dimension_custom_largeur);
            const lon = Number(state.dimension_custom_longueur);
            finalDimension = `${lar} × ${lon} cm (sur-mesure)`;
            dimensionCustom = { largeur: lar, longueur: lon };
        }

        return {
            submission_id: submissionIdRef.current,
            sleepers: sleepers.map((s) => {
                const { discomfort_other: _unused, ...rest } = s;
                return { ...rest, sensation: rest.sensation || sleepers[0].sensation || "inconnu" };
            }),
            heat: heatAgg,
            movement_ease: moveAgg,
            partner: nbSleepers === 2 ? partnerAgg || "pas" : null,
            current_mattress_type: state.current_mattress_no_answer ? null : (state.current_mattress_type || null),
            change_reasons: state.current_mattress_no_answer ? [] : state.change_reasons,
            discomfort_areas: discomfortUnion,
            dimension: finalDimension,
            dimension_custom: dimensionCustom,
            other_texts: Object.keys(otherTexts).length > 0 ? otherTexts : null,
            budget: state.budget_priority_confort ? null : (Number(state.budget) > 0 ? Number(state.budget) : null),
            budget_priority_confort: !!state.budget_priority_confort,
            prenom: state.prenom || null,
            nom: state.nom || null,
            telephone: state.telephone || null,
            email: state.email || null,
            consent_email: !!state.email,
        };
    };

    const submit = async () => {
        if (submitting) return; // hard guard on top of the button-disabled state
        setSubmitting(true);
        try {
            const { data } = await api.post("/questionnaires", buildPayload());
            navigate(`/resultat/${data.id}`);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setSubmitting(false);
        }
    };

    // Phase 4: from the last question step, go to the review page.
    const goToReview = () => {
        const now = Date.now();
        if (now - lastNavAt.current < NAV_MIN_INTERVAL_MS) return;
        if (!canNext() || isTransitioning || submitting) return;
        lastNavAt.current = now;
        setShowReview(true);
    };

    // Phase 4: from the review page, jump back to edit a single block.
    const editStep = (stepName) => {
        const idx = steps.indexOf(stepName);
        if (idx < 0) return;
        setShowReview(false);
        setStep(idx);
    };

    // Phase 4: called by the review "Calculer mon profil" CTA.
    const onCalculate = () => {
        if (submitting) return;
        const conflicts = detectContradictions(sleepers, state, nbSleepers);
        if (conflicts.length > 0) {
            setDetectedContradictions(conflicts);
            setContradictionsOpen(true);
            return;
        }
        submit();
    };

    return (
        <div className="min-h-screen bg-brand-beige flex flex-col" data-testid="questionnaire-page">
            {/* Top bar with progress */}
            <div className="bg-white/70 backdrop-blur sticky top-0 z-10 border-b border-brand-ink/5">
                <div className="max-w-3xl mx-auto px-6 py-4 flex items-center gap-4">
                    <Link to="/" className="text-sm text-brand-ink/60 hover:text-[#2A84C8]">
                        ← Accueil
                    </Link>
                    <div className="flex-1 h-2 bg-brand-ink/10 rounded-full overflow-hidden">
                        <div
                            className="h-full bg-[#2A84C8] transition-all duration-500"
                            style={{ width: `${progress}%` }}
                            data-testid="progress-bar"
                        />
                    </div>
                    <span className="text-sm text-brand-ink/60 tabular-nums w-24 text-right" data-testid="step-counter">
                        {showReview ? "Vérification" : (showTotal ? `${step + 1} / ${totalDisplayed}` : `Étape ${step + 1}`)}
                    </span>
                </div>
            </div>

            <div className="flex-1 max-w-3xl w-full mx-auto px-6 py-10 md:py-16 flex flex-col">
                {showReview ? (
                    <ReviewPage
                        nbSleepers={nbSleepers}
                        sleepers={sleepers}
                        state={state}
                        submitting={submitting}
                        onEdit={editStep}
                        onCalculate={onCalculate}
                        onBack={() => setShowReview(false)}
                    />
                ) : (
                <>
                <AnimatePresence mode="wait">
                    <motion.div
                        key={current}
                        initial={{ opacity: 0, x: 16 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: -16 }}
                        transition={{ duration: 0.18 }}
                        onAnimationStart={() => { try { window.scrollTo({ top: 0, behavior: "instant" }); } catch(_) { window.scrollTo(0,0); } }}
                        onAnimationComplete={() => setIsTransitioning(false)}
                        className="flex-1"
                    >
                        {current === "nb_sleepers" && (
                            <Question title="Ce matelas est destiné à :" testid="q-nb-sleepers">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <button className="kiosk-btn" data-active={nbSleepers === 1} onClick={() => setNb(1)} data-testid="opt-one-sleeper">
                                        Une personne
                                    </button>
                                    <button className="kiosk-btn" data-active={nbSleepers === 2} onClick={() => setNb(2)} data-testid="opt-two-sleepers">
                                        Deux personnes
                                    </button>
                                </div>
                            </Question>
                        )}

                        {current === "morphology" && (
                            <Question title="Taille et poids" subtitle="Renseignez ces informations pour chaque dormeur (taille : 100 à 230 cm · poids : 30 à 200 kg).">
                                <div className="space-y-6">
                                    {sleepers.map((s, i) => (
                                        <div key={i} className="bg-white rounded-2xl p-5 border border-brand-ink/10">
                                            <p className="font-medium text-brand-ink mb-4">{s.label}</p>
                                            <div className="grid grid-cols-2 gap-4">
                                                <NumberInput label="Taille (cm)" value={s.taille} min={100} max={230} onChange={(v) => updateSleeper(i, { taille: v })} testid={`taille-${i}`} />
                                                <NumberInput label="Poids (kg)" value={s.poids} min={30} max={200} onChange={(v) => updateSleeper(i, { poids: v })} testid={`poids-${i}`} />
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </Question>
                        )}

                        {current === "position" && (
                            <Question title="Dans quelle position dormez-vous le plus souvent ?">
                                <div className="space-y-6">
                                    {sleepers.map((s, i) => (
                                        <div key={i}>
                                            {sleepers.length > 1 && <p className="font-medium text-brand-ink mb-3">{s.label}</p>}
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                                {POSITIONS.map((p) => (
                                                    <button key={p.v} className="kiosk-btn" data-active={s.position === p.v} onClick={() => updateSleeper(i, { position: p.v })} data-testid={`pos-${i}-${p.v}`}>
                                                        {p.l}
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </Question>
                        )}

                        {current === "sensation" && (
                            <Question title="Lorsque vous vous allongez sur un matelas, quelle sensation préférez-vous ?">
                                <div className="space-y-6">
                                    {sleepers.map((s, i) => (
                                        <div key={i}>
                                            {sleepers.length > 1 && <p className="font-medium text-brand-ink mb-3">{s.label}</p>}
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                                {SENSATIONS.map((p) => (
                                                    <button key={p.v} className="kiosk-btn" data-active={s.sensation === p.v} onClick={() => updateSleeper(i, { sensation: p.v })} data-testid={`sens-${i}-${p.v}`}>
                                                        {p.l}
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </Question>
                        )}

                        {current === "envelopment" && (
                            <Question title="Aimez-vous sentir votre corps légèrement enveloppé par le matelas ?" subtitle="1 = Pas du tout · 5 = Beaucoup">
                                <div className="space-y-6">
                                    {sleepers.map((s, i) => (
                                        <div key={i}>
                                            {sleepers.length > 1 && <p className="font-medium text-brand-ink mb-3">{s.label}</p>}
                                            <div className="grid grid-cols-5 gap-3">
                                                {[1, 2, 3, 4, 5].map((n) => (
                                                    <button key={n} className="kiosk-btn justify-center text-2xl font-heading" data-active={s.envelopment === n} onClick={() => updateSleeper(i, { envelopment: n })} data-testid={`env-${i}-${n}`}>
                                                        {n}
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </Question>
                        )}

                        {current === "movement_ease" && (
                            <Question title="Est-il important pour vous de pouvoir changer facilement de position pendant la nuit ?">
                                <div className="space-y-6">
                                    {sleepers.map((s, i) => (
                                        <div key={i}>
                                            {sleepers.length > 1 && <p className="font-medium text-brand-ink mb-3">{s.label}</p>}
                                            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                                                {MOVEMENT.map((p) => (
                                                    <button key={p.v} className="kiosk-btn" data-active={s.movement_ease === p.v} onClick={() => updateSleeper(i, { movement_ease: p.v })} data-testid={`move-${i}-${p.v}`}>
                                                        {p.l}
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </Question>
                        )}

                        {current === "heat" && (
                            <Question title="Avez-vous tendance à avoir chaud pendant la nuit ?">
                                <div className="space-y-6">
                                    {sleepers.map((s, i) => (
                                        <div key={i}>
                                            {sleepers.length > 1 && <p className="font-medium text-brand-ink mb-3">{s.label}</p>}
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                                {HEATS.map((p) => (
                                                    <button key={p.v} className="kiosk-btn" data-active={s.heat === p.v} onClick={() => updateSleeper(i, { heat: p.v })} data-testid={`heat-${i}-${p.v}`}>
                                                        {p.l}
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </Question>
                        )}

                        {current === "partner" && (
                            <Question title="Êtes-vous gêné(e) par les mouvements de votre partenaire ?">
                                <div className="space-y-6">
                                    {sleepers.map((s, i) => (
                                        <div key={i}>
                                            {sleepers.length > 1 && <p className="font-medium text-brand-ink mb-3">{s.label}</p>}
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                                {PARTNER.map((p) => (
                                                    <button key={p.v} className="kiosk-btn" data-active={s.partner === p.v} onClick={() => updateSleeper(i, { partner: p.v })} data-testid={`partner-${i}-${p.v}`}>
                                                        {p.l}
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </Question>
                        )}

                        {current === "current_mattress" && (
                            <Question title="Quel type de matelas utilisez-vous actuellement ?" subtitle="Puis dites-nous ce qui vous pousse à en changer (plusieurs choix possibles).">
                                <div className={`space-y-6 ${state.current_mattress_no_answer ? "opacity-40 pointer-events-none" : ""}`}>
                                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                                        {CURRENT_TYPES.map((t) => (
                                            <button key={t} className="kiosk-btn" data-active={state.current_mattress_type === t} onClick={() => setState({ ...state, current_mattress_type: t })} data-testid={`cur-${t}`}>
                                                {t}
                                            </button>
                                        ))}
                                    </div>
                                    <div>
                                        <p className="text-sm text-brand-ink/60 mb-3">Raisons du changement :</p>
                                        <MultiSelect items={CHANGE_REASONS} value={state.change_reasons} onChange={(v) => setState({ ...state, change_reasons: v, change_reason_other: v.includes("Autre") ? state.change_reason_other : "" })} testidPrefix="reason" />
                                        {(state.change_reasons || []).includes("Autre") && (
                                            <div className="mt-4">
                                                <OtherPrecision
                                                    label="Précisez la raison"
                                                    value={state.change_reason_other}
                                                    onChange={(v) => setState({ ...state, change_reason_other: v })}
                                                    testid="change-reason-other"
                                                />
                                            </div>
                                        )}
                                    </div>
                                </div>
                                <div className="mt-6 pt-6 border-t border-brand-ink/10">
                                    <button
                                        className="kiosk-btn"
                                        data-active={state.current_mattress_no_answer}
                                        onClick={() => setState({ ...state, current_mattress_no_answer: !state.current_mattress_no_answer, current_mattress_type: state.current_mattress_no_answer ? state.current_mattress_type : "", change_reasons: state.current_mattress_no_answer ? state.change_reasons : [], change_reason_other: "" })}
                                        data-testid="current-mattress-no-answer"
                                    >
                                        Je préfère ne pas répondre
                                    </button>
                                </div>
                            </Question>
                        )}

                        {current === "discomfort" && (
                            <Question title="Ressentez-vous parfois une gêne ou des tensions au réveil ?" subtitle="Question facultative. Cette information sert uniquement à adapter le conseil de confort et ne constitue pas un diagnostic médical.">
                                <div className="space-y-6">
                                    {sleepers.map((s, i) => (
                                        <div key={i}>
                                            {sleepers.length > 1 && <p className="font-medium text-brand-ink mb-3">{s.label}</p>}
                                            <MultiSelect
                                                items={DISCOMFORT}
                                                value={s.discomfort_areas || []}
                                                onChange={(v) => {
                                                    const filtered = applyDiscomfortExclusive(s.discomfort_areas || [], v);
                                                    updateSleeper(i, {
                                                        discomfort_areas: filtered,
                                                        // Drop stale "Autre" precision if "Autre" was removed by exclusivity.
                                                        discomfort_other: filtered.includes("Autre") ? s.discomfort_other : "",
                                                    });
                                                }}
                                                testidPrefix={`dis-${i}`}
                                            />
                                            {(s.discomfort_areas || []).includes("Autre") && (
                                                <div className="mt-4">
                                                    <OtherPrecision
                                                        label="Précisez votre gêne"
                                                        value={s.discomfort_other || ""}
                                                        onChange={(v) => updateSleeper(i, { discomfort_other: v })}
                                                        testid={`discomfort-other-${i}`}
                                                    />
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </Question>
                        )}

                        {current === "dimension" && (
                            <Question title="Quelle dimension de matelas recherchez-vous ?">
                                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                                    {DIMENSIONS.map((d) => (
                                        <button key={d} className="kiosk-btn justify-center" data-active={state.dimension === d} onClick={() => setState({ ...state, dimension: d, dimension_custom_largeur: d === "Autre" ? state.dimension_custom_largeur : "", dimension_custom_longueur: d === "Autre" ? state.dimension_custom_longueur : "" })} data-testid={`dim-${d.replace(/\s|×/g, "")}`}>
                                            {d}
                                        </button>
                                    ))}
                                </div>
                                {state.dimension === "Autre" && (
                                    <div className="mt-6 bg-white rounded-2xl p-6 border border-brand-ink/10 space-y-4" data-testid="dimension-custom-block">
                                        <p className="text-sm text-brand-ink/70">Saisissez la dimension souhaitée (en cm). Aucun prix ne pourra être affiché pour une taille sur-mesure.</p>
                                        <div className="grid grid-cols-2 gap-4">
                                            <div>
                                                <NumberInput label="Largeur (cm)" value={state.dimension_custom_largeur} min={DIM_MIN} max={DIM_MAX} onChange={(v) => setState({ ...state, dimension_custom_largeur: v })} testid="dim-custom-largeur" />
                                                {state.dimension_custom_largeur !== "" && !inDimRange(state.dimension_custom_largeur) && (
                                                    <p className="text-xs text-red-600 mt-1" data-testid="dim-largeur-error">Largeur invalide (attendu : {DIM_MIN}-{DIM_MAX} cm).</p>
                                                )}
                                            </div>
                                            <div>
                                                <NumberInput label="Longueur (cm)" value={state.dimension_custom_longueur} min={DIM_MIN} max={DIM_MAX} onChange={(v) => setState({ ...state, dimension_custom_longueur: v })} testid="dim-custom-longueur" />
                                                {state.dimension_custom_longueur !== "" && !inDimRange(state.dimension_custom_longueur) && (
                                                    <p className="text-xs text-red-600 mt-1" data-testid="dim-longueur-error">Longueur invalide (attendu : {DIM_MIN}-{DIM_MAX} cm).</p>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </Question>
                        )}

                        {current === "budget" && (
                            <Question title="Quel budget souhaitez-vous consacrer à votre nouveau matelas ?" subtitle="Indication facultative. Le budget n'affecte jamais notre analyse du confort — il sert uniquement à préciser l'orientation.">
                                <div className="space-y-6">
                                    <div className="bg-white rounded-2xl p-6 border border-brand-ink/10">
                                        <label className="block">
                                            <span className="block text-sm text-brand-ink/70 mb-2">Budget indicatif (€)</span>
                                            <div className="relative">
                                                <input
                                                    type="number"
                                                    inputMode="numeric"
                                                    min={0}
                                                    step={50}
                                                    value={state.budget}
                                                    onChange={(e) => setState({ ...state, budget: e.target.value, budget_priority_confort: false })}
                                                    placeholder="ex : 1500"
                                                    className="w-full min-h-[64px] rounded-xl border border-brand-ink/15 pr-12 pl-4 text-2xl font-medium tabular-nums focus:outline-none focus:ring-2 focus:ring-[#2A84C8]/30 focus:border-[#2A84C8] bg-brand-beige/40"
                                                    data-testid="input-budget"
                                                />
                                                <span className="absolute right-4 top-1/2 -translate-y-1/2 text-brand-ink/50 text-xl">€</span>
                                            </div>
                                        </label>
                                    </div>
                                    <div className="relative flex items-center gap-3">
                                        <div className="flex-1 h-px bg-brand-ink/10" />
                                        <span className="text-xs uppercase tracking-widest text-brand-ink/40">ou</span>
                                        <div className="flex-1 h-px bg-brand-ink/10" />
                                    </div>
                                    <button
                                        className="kiosk-btn"
                                        data-active={state.budget_priority_confort}
                                        onClick={() => setState({ ...state, budget_priority_confort: !state.budget_priority_confort, budget: "" })}
                                        data-testid="budget-priority-confort"
                                    >
                                        Je préfère d'abord choisir selon le confort
                                    </button>
                                </div>
                            </Question>
                        )}

                        {current === "contact" && (
                            <Question title="Souhaitez-vous recevoir votre profil par email ?" subtitle="Ces informations sont facultatives. Vous verrez votre résultat même sans les fournir.">
                                <div className="space-y-4 bg-white rounded-2xl p-6 border border-brand-ink/10">
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <TextInput label="Prénom" value={state.prenom} onChange={(v) => setState({ ...state, prenom: v })} testid="prenom" />
                                        <TextInput label="Nom" value={state.nom} onChange={(v) => setState({ ...state, nom: v })} testid="nom" />
                                    </div>
                                    <div>
                                        <TextInput label="Téléphone" value={state.telephone} onChange={(v) => setState({ ...state, telephone: v })} testid="telephone" />
                                        {state.telephone && !validPhone(state.telephone) && (
                                            <p className="text-xs text-red-600 mt-1" data-testid="telephone-error">Format invalide (ex : 06 12 34 56 78 ou +33 6 12 34 56 78).</p>
                                        )}
                                    </div>
                                    <div>
                                        <TextInput label="Email" type="email" value={state.email} onChange={(v) => setState({ ...state, email: v })} testid="email" />
                                        {state.email && !validEmail(state.email) && (
                                            <p className="text-xs text-red-600 mt-1" data-testid="email-error">Adresse email invalide.</p>
                                        )}
                                    </div>
                                    <p className="text-xs text-brand-ink/50 pt-2 border-t border-brand-ink/10">
                                        Une question ? Appelez la boutique au{" "}
                                        <a href="tel:+33677548220" className="text-[#2A84C8] font-medium hover:underline" data-testid="contact-shop-phone">06 77 54 82 20</a>.
                                    </p>
                                </div>
                            </Question>
                        )}
                    </motion.div>
                </AnimatePresence>

                <div className="flex items-center justify-between mt-10 gap-4">
                    <button
                        onClick={prev}
                        disabled={step === 0 || isTransitioning || submitting}
                        className="ghost-btn"
                        data-testid="prev-btn"
                    >
                        <ArrowLeft className="w-4 h-4" /> Précédent
                    </button>
                    <button
                        onClick={next}
                        disabled={!canNext() || submitting || isTransitioning}
                        className="primary-btn"
                        data-testid="next-btn"
                    >
                        {step === steps.length - 1 ? "Vérifier mes réponses" : "Suivant"}
                        <ArrowRight className="w-5 h-5" />
                    </button>
                </div>
                </>
                )}
            </div>

            {/* Phase 4 — Contradiction modal */}
            <ContradictionModal
                open={contradictionsOpen}
                items={detectedContradictions}
                onReview={() => { setContradictionsOpen(false); }}
                onContinue={() => { setContradictionsOpen(false); submit(); }}
                submitting={submitting}
            />
        </div>
    );
}

function Question({ title, subtitle, children, testid }) {
    return (
        <div data-testid={testid}>
            <h2 className="font-heading text-2xl md:text-3xl lg:text-4xl font-semibold text-brand-ink mb-3">
                {title}
            </h2>
            {subtitle && <p className="text-base text-brand-ink/60 mb-8">{subtitle}</p>}
            {!subtitle && <div className="mb-8" />}
            {children}
        </div>
    );
}

function MultiSelect({ items, value, onChange, testidPrefix }) {
    const toggle = (it) =>
        value.includes(it) ? onChange(value.filter((x) => x !== it)) : onChange([...value, it]);
    return (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {items.map((it) => (
                <button key={it} className="kiosk-btn" data-active={value.includes(it)} onClick={() => toggle(it)} data-testid={`${testidPrefix}-${slug(it)}`}>
                    {it}
                </button>
            ))}
        </div>
    );
}

function NumberInput({ label, value, onChange, min, max, testid }) {
    return (
        <label className="block">
            <span className="block text-sm text-brand-ink/60 mb-2">{label}</span>
            <input
                type="number"
                inputMode="numeric"
                value={value}
                min={min}
                max={max}
                placeholder={`${min} - ${max}`}
                onChange={(e) => {
                    const raw = e.target.value;
                    if (raw === "") { onChange(""); return; }
                    const n = Number(raw);
                    onChange(Number.isNaN(n) ? "" : n);
                }}
                className="w-full min-h-[60px] rounded-xl border border-brand-ink/15 px-4 text-xl font-medium focus:outline-none focus:ring-2 focus:ring-[#2A84C8]/30 focus:border-[#2A84C8] bg-brand-beige/50"
                data-testid={`input-${testid}`}
            />
        </label>
    );
}

function TextInput({ label, value, onChange, type = "text", testid }) {
    return (
        <label className="block">
            <span className="block text-sm text-brand-ink/60 mb-1">{label}</span>
            <input
                type={type}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                className="w-full min-h-[52px] rounded-xl border border-brand-ink/15 px-4 focus:outline-none focus:ring-2 focus:ring-[#2A84C8]/30 focus:border-[#2A84C8]"
                data-testid={`input-${testid}`}
            />
        </label>
    );
}

function slug(s) {
    return s.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
}

function OtherPrecision({ label, value, onChange, testid }) {
    const trimmed = (value || "").trim();
    const invalid = (value || "").length > 0 && trimmed.length === 0;
    return (
        <label className="block bg-brand-beige/40 rounded-xl p-4 border border-[#2A84C8]/20">
            <span className="block text-sm text-brand-ink/70 mb-2">{label} <span className="text-red-600">*</span></span>
            <input
                type="text"
                value={value || ""}
                maxLength={200}
                placeholder="Votre réponse"
                onChange={(e) => onChange(e.target.value)}
                className="w-full min-h-[52px] rounded-xl border border-brand-ink/15 px-4 focus:outline-none focus:ring-2 focus:ring-[#2A84C8]/30 focus:border-[#2A84C8] bg-white"
                data-testid={`input-${testid}`}
            />
            <div className="flex items-center justify-between mt-1">
                {invalid ? (
                    <span className="text-xs text-red-600" data-testid={`${testid}-error`}>Merci de saisir une réponse (les espaces seuls ne sont pas acceptés).</span>
                ) : <span />}
                <span className="text-xs text-brand-ink/40 tabular-nums">{(value || "").length}/200</span>
            </div>
        </label>
    );
}

// Phase 4 — Review page. Grouped read-only summary + per-block Edit buttons.
function ReviewPage({ nbSleepers, sleepers, state, submitting, onEdit, onCalculate, onBack }) {
    const escape = (s) => (typeof s === "string" ? s : "");
    return (
        <div className="flex-1 flex flex-col" data-testid="review-page">
            <h2 className="font-heading text-2xl md:text-3xl lg:text-4xl font-semibold text-brand-ink mb-3">
                Vérification de vos réponses
            </h2>
            <p className="text-base text-brand-ink/60 mb-8">
                Relisez rapidement vos réponses. Vous pouvez modifier chaque bloc avant de calculer votre profil.
            </p>

            <div className="space-y-4">
                <Block title="Nombre de dormeurs" onEdit={() => onEdit("nb_sleepers")} testid="review-nb">
                    <Item label="Destinataire" value={nbSleepers === 2 ? "Deux personnes" : "Une personne"} />
                </Block>

                {sleepers.map((s, i) => (
                    <Block key={i} title={nbSleepers === 2 ? `Morphologie — ${s.label}` : "Morphologie"} onEdit={() => onEdit("morphology")} testid={`review-morpho-${i}`}>
                        <Item label="Taille" value={`${s.taille} cm`} />
                        <Item label="Poids" value={`${s.poids} kg`} />
                    </Block>
                ))}

                <Block title="Position de sommeil" onEdit={() => onEdit("position")} testid="review-position">
                    {sleepers.map((s, i) => (
                        <Item key={i} label={nbSleepers === 2 ? s.label : "Position"} value={LABEL.position[s.position] || "—"} />
                    ))}
                </Block>

                <Block title="Sensation préférée" onEdit={() => onEdit("sensation")} testid="review-sensation">
                    {sleepers.map((s, i) => (
                        <Item key={i} label={nbSleepers === 2 ? s.label : "Sensation"} value={LABEL.sensation[s.sensation] || "—"} />
                    ))}
                </Block>

                <Block title="Enveloppement" onEdit={() => onEdit("envelopment")} testid="review-envelopment">
                    {sleepers.map((s, i) => (
                        <Item key={i} label={nbSleepers === 2 ? s.label : "Enveloppement"} value={s.envelopment != null ? `${s.envelopment} / 5` : "—"} />
                    ))}
                </Block>

                <Block title="Facilité de mouvement" onEdit={() => onEdit("movement_ease")} testid="review-movement">
                    {sleepers.map((s, i) => (
                        <Item key={i} label={nbSleepers === 2 ? s.label : "Importance"} value={LABEL.movement[s.movement_ease] || "—"} />
                    ))}
                </Block>

                <Block title="Chaleur ressentie la nuit" onEdit={() => onEdit("heat")} testid="review-heat">
                    {sleepers.map((s, i) => (
                        <Item key={i} label={nbSleepers === 2 ? s.label : "Chaleur"} value={LABEL.heat[s.heat] || "—"} />
                    ))}
                </Block>

                {nbSleepers === 2 && (
                    <Block title="Indépendance de couchage (mouvements du partenaire)" onEdit={() => onEdit("partner")} testid="review-partner">
                        {sleepers.map((s, i) => (
                            <Item key={i} label={s.label} value={LABEL.partner[s.partner] || "—"} />
                        ))}
                    </Block>
                )}

                <Block title="Gênes déclarées" onEdit={() => onEdit("discomfort")} testid="review-discomfort">
                    {sleepers.map((s, i) => {
                        const list = (s.discomfort_areas || []);
                        const shown = list.length ? list.join(", ") : "aucune";
                        const other = list.includes("Autre") && s.discomfort_other ? ` — précision : « ${escape(s.discomfort_other)} »` : "";
                        return <Item key={i} label={nbSleepers === 2 ? s.label : "Zones"} value={`${shown}${other}`} />;
                    })}
                </Block>

                <Block title="Matelas actuel & raisons du changement" onEdit={() => onEdit("current_mattress")} testid="review-current">
                    {state.current_mattress_no_answer ? (
                        <Item label="Réponse" value="Je préfère ne pas répondre" />
                    ) : (
                        <>
                            <Item label="Type" value={state.current_mattress_type || "—"} />
                            <Item label="Raisons" value={(state.change_reasons || []).join(", ") || "—"} />
                            {(state.change_reasons || []).includes("Autre") && state.change_reason_other && (
                                <Item label="Précision « Autre »" value={`« ${escape(state.change_reason_other)} »`} />
                            )}
                        </>
                    )}
                </Block>

                <Block title="Dimension" onEdit={() => onEdit("dimension")} testid="review-dimension">
                    {state.dimension === "Autre" ? (
                        <Item
                            label="Dimension sur-mesure"
                            value={`${state.dimension_custom_largeur} × ${state.dimension_custom_longueur} cm`}
                        />
                    ) : (
                        <Item label="Choix" value={state.dimension || "—"} />
                    )}
                </Block>

                <Block title="Budget" onEdit={() => onEdit("budget")} testid="review-budget">
                    {state.budget_priority_confort ? (
                        <Item label="Choix" value="Je préfère d'abord choisir selon le confort" />
                    ) : (
                        <Item label="Budget indicatif" value={state.budget ? `${state.budget} €` : "—"} />
                    )}
                </Block>

                <Block title="Coordonnées" onEdit={() => onEdit("contact")} testid="review-contact">
                    <Item label="Prénom" value={state.prenom || "—"} />
                    <Item label="Nom" value={state.nom || "—"} />
                    <Item label="Téléphone" value={state.telephone || "—"} />
                    <Item label="Email" value={state.email || "—"} />
                </Block>
            </div>

            <div className="flex items-center justify-between mt-10 gap-4">
                <button onClick={onBack} disabled={submitting} className="ghost-btn" data-testid="review-back">
                    <ArrowLeft className="w-4 h-4" /> Retour aux questions
                </button>
                <button onClick={onCalculate} disabled={submitting} className="primary-btn" data-testid="calculate-btn">
                    {submitting ? "Analyse en cours…" : "Calculer mon profil"}
                    <ArrowRight className="w-5 h-5" />
                </button>
            </div>
        </div>
    );
}

function Block({ title, onEdit, testid, children }) {
    return (
        <div className="bg-white rounded-2xl border border-brand-ink/10 p-5" data-testid={testid}>
            <div className="flex items-center justify-between mb-3">
                <h3 className="font-heading text-lg text-brand-ink">{title}</h3>
                <button onClick={onEdit} className="text-sm text-[#2A84C8] font-medium inline-flex items-center gap-1 hover:underline" data-testid={`${testid}-edit`}>
                    <Pencil className="w-4 h-4" /> Modifier
                </button>
            </div>
            <div className="grid grid-cols-1 gap-1 text-sm text-brand-ink/80">
                {children}
            </div>
        </div>
    );
}

function Item({ label, value }) {
    return (
        <div className="flex flex-wrap justify-between gap-x-4 border-b border-brand-ink/5 last:border-0 py-1.5">
            <span className="text-brand-ink/50">{label}</span>
            <span className="text-brand-ink font-medium text-right">{value}</span>
        </div>
    );
}

// Phase 4 — Contradiction modal (non-blocking, neutral copy).
function ContradictionModal({ open, items, onReview, onContinue, submitting }) {
    if (!open) return null;
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" role="dialog" aria-modal="true" data-testid="contradiction-modal">
            <div className="bg-white rounded-3xl max-w-lg w-full p-6 shadow-2xl">
                <div className="flex items-start gap-3 mb-4">
                    <div className="w-10 h-10 rounded-full bg-[#2A84C8]/10 flex items-center justify-center text-[#2A84C8] shrink-0">
                        <AlertTriangle className="w-5 h-5" />
                    </div>
                    <div>
                        <h3 className="font-heading text-xl text-brand-ink">Quelques réponses à confirmer</h3>
                        <p className="text-sm text-brand-ink/60 mt-1">
                            Certaines de vos réponses semblent exprimer des besoins différents. Vous pouvez les vérifier ou continuer si elles correspondent bien à votre ressenti.
                        </p>
                    </div>
                </div>
                <ul className="list-disc pl-5 space-y-1 text-sm text-brand-ink/80 mb-6" data-testid="contradiction-list">
                    {items.map((t, i) => <li key={i}>{t}</li>)}
                </ul>
                <div className="flex flex-col sm:flex-row gap-3">
                    <button onClick={onReview} className="ghost-btn flex-1 justify-center" data-testid="contradiction-review">
                        Revoir mes réponses
                    </button>
                    <button onClick={onContinue} disabled={submitting} className="primary-btn flex-1 justify-center" data-testid="contradiction-continue">
                        {submitting ? "Analyse en cours…" : "Continuer malgré tout"}
                    </button>
                </div>
            </div>
        </div>
    );
}
